import React, { useState, useEffect, useMemo } from "react";

import {
  getBookingOpenBoundary,
  getBookingCloseBoundary,
} from "./BoatsBooking";

// [Insert the formatTimeDifference helper function here]

// Define the component signature to accept the required booking rules
const BookingTimer = ({
  selectedDate, // The date we are checking status for (e.g., '2025-12-14')
  advanceBookingDays,
  bookingOpenTime, // HH:MM string
  bookingCloseTime, // HH:MM string
}) => {
  // --- State and Effect (Unchanged) ---
  const [currentTime, setCurrentTime] = useState(Date.now());

  useEffect(() => {
    const intervalId = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(intervalId);
  }, []);
  // ------------------------------------

  // --- Date Formatting for Rendering ---
  // Convert the Moment object prop into a display string here
  const displayDateString = selectedDate.format("dddd, MMMM D, YYYY");

  // Convert the Moment object to the native string format needed by the helper functions
  // (We assumed the helper functions were updated to accept Moment, but here we explicitly
  // grab the ISO string for robust use if helpers were not fully Moment-proofed).
  const selectedDateISOString = selectedDate.toISOString();

  // Memoize boundary calculations
  const {
    fullDate: openTimeAbsolute,
    dateStr: openDateStr,
    timeStr: openTimeStr,
  } = useMemo(
    // Use the ISO string format of the selected date for the helper function calculation
    () =>
      getBookingOpenBoundary(
        selectedDateISOString,
        advanceBookingDays,
        bookingOpenTime
      ),
    [selectedDateISOString, advanceBookingDays, bookingOpenTime]
  );

  const { fullDate: closeTimeAbsolute, timeStr: closeTimeStr } = useMemo(
    () =>
      getBookingCloseBoundary(
        selectedDateISOString,
        advanceBookingDays,
        bookingCloseTime
      ),
    [selectedDateISOString, advanceBookingDays, bookingCloseTime]
  );

  // Calculate status based on current time
  const status = useMemo(() => {
    const now = currentTime;

    // 1. Booking has not opened yet
    if (now < openTimeAbsolute.getTime()) {
      const timeUntilOpen = openTimeAbsolute.getTime() - now;
      return {
        statusText: `Booking opens in ${formatTimeDifference(
          timeUntilOpen
        )} (on ${openDateStr} at ${openTimeStr})`,
        statusColor: "text-blue-500",
      };
    }

    // 2. Booking is currently open
    if (
      now >= openTimeAbsolute.getTime() &&
      now < closeTimeAbsolute.getTime()
    ) {
      const timeUntilClose = closeTimeAbsolute.getTime() - now;
      return {
        statusText: `Booking closes in ${formatTimeDifference(timeUntilClose)}`,
        statusColor: "green",
      };
    }

    // 3. Booking is closed

    const nextDay = new Date(openTimeAbsolute);
    nextDay.setDate(nextDay.getDate() + 1);

    if (now >= nextDay.getTime()) {
      // If the entire booking day has passed
      return {
        statusText: `Booking closed for this date.`,
        statusColor: "gray",
      };
    } else {
      // If booking closed today at the close time
      return {
        statusText: `Booking closed for this date.`,
        statusColor: "gray",
      };
    }
  }, [
    currentTime,
    openTimeAbsolute,
    closeTimeAbsolute,
    openDateStr,
    openTimeStr,
    closeTimeStr,
    displayDateString,
  ]);

  return (
    <p style={{ fontWeight: "bold", margin: 0, color: status.statusColor }}>
      {status.statusText}
    </p>
  );
};

export default BookingTimer;

/**
 * Converts a time difference in milliseconds into a readable countdown string.
 * @param {number} ms - Time difference in milliseconds (can be negative).
 * @returns {string} The formatted time string (e.g., "1d 2h 45m" or "15 mins ago").
 */
function formatTimeDifference(ms) {
  const isPast = ms < 0;
  const absMs = Math.abs(ms);

  if (absMs < 1000) return "just now";

  const seconds = Math.floor(absMs / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  // Calculate remainder units
  const s = seconds % 60;
  const m = minutes % 60;
  const h = hours % 24;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (h > 0) parts.push(`${h}h`);
  if (m > 0) parts.push(`${m}m`);
  // Only show seconds if the countdown is under an hour for conciseness
  if (h === 0 && days === 0) {
    if (m === 0 && s > 0) parts.push(`${s}s`);
    else if (m > 0) parts.push(`${s}s`);
  }

  const timeString = parts.join(" ");

  // Handle the state descriptions
  if (isPast) {
    if (days > 0) return "Closed"; // For booking purposes, if the day passed, just say Closed
    return `Closed ${timeString ? timeString + " ago" : ""}`;
  }

  return timeString || "Less than a second"; // Should return a time string if > 1s
}
